// ─────────────────────────────────────────────────────────────────────────────
// keytelCalories.js — Keytel formula for HR-based calorie estimation.
//
// Reference:
//   Keytel LR et al. (2005) "Prediction of energy expenditure from heart rate
//   monitoring during submaximal exercise." J Sports Sci 23(3):289–97.
//
// Formulas (kcal/minute):
//
//   Male:   ((-55.0969 + 0.6309 × HR + 0.1988 × W + 0.2017 × A) / 4.184)
//   Female: ((-20.4022 + 0.4472 × HR − 0.1263 × W + 0.0740 × A) / 4.184)
//
// where HR is bpm, W is body mass in kg, A is age in years.
//
// CrewZ usage:
//   - Phone Mode (Mode 2): tick every 60 s while workout is active+unpaused;
//     each tick accumulates kcalPerMinute(...) into the cumulative `calories`
//     state so the meter rises monotonically across the whole session.
//   - Ergo Mode (Mode 3): NOT used — PM5 reports its own calorie value.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute age in whole years from an ISO-formatted date-of-birth string.
 * Returns null when DOB is missing or invalid so callers can short-circuit.
 *
 * @param {string|undefined|null} dobIso  e.g. "1985-07-23"
 * @returns {number|null}
 */
export function ageFromDob(dobIso) {
  if (!dobIso) return null;
  const dob = new Date(dobIso);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years -= 1;
  return years >= 0 ? years : null;
}

/**
 * Keytel kcal-per-minute estimate.
 *
 * Returns 0 when any input is missing or invalid — the caller can then choose
 * to skip the cumulative tick (no fabricated calories).
 *
 * @param {object} args
 * @param {number} args.heartRate     bpm (must be > 0; treat 0 / 255 as "no signal")
 * @param {number} args.weightKg      body mass in kg
 * @param {number} args.ageYears      age in whole years
 * @param {'male'|'female'|string} args.sex
 * @returns {number}                  kcal/min (>= 0, never negative)
 */
export function keytelKcalPerMinute({ heartRate, weightKg, ageYears, sex }) {
  if (!heartRate || heartRate <= 0 || heartRate >= 255) return 0;
  if (!weightKg || weightKg <= 0) return 0;
  if (ageYears == null || ageYears <= 0) return 0;

  const HR = heartRate;
  const W = weightKg;
  const A = ageYears;
  const isMale = typeof sex === 'string' && sex.toLowerCase().startsWith('m');

  const raw = isMale
    ? (-55.0969 + 0.6309 * HR + 0.1988 * W + 0.2017 * A) / 4.184
    : (-20.4022 + 0.4472 * HR - 0.1263 * W + 0.0740 * A) / 4.184;

  // Clamp at zero — the linear formula can go negative at very low HR for
  // light/young subjects, which would erode the cumulative calorie counter.
  return raw > 0 ? raw : 0;
}
